import {paginatedListAction} from '@/vuex/paginatedListAction';
import mutations from './mutations';
import {notification} from "ant-design-vue";
import {DataService} from "../../../config/dataService/dataService";

const addNotificationError = (err) => {
    notification.error({
        message: err,
    });
};

const addNotificationSuccess = (err) => {
    notification.success({
        message: err,
    });
};

const state = () => ({
    listQuery: {},
    template: {
        id: null,
        name: null,
        html: '',
        type: null,
        productType: null,
        hasMargin: false,
    },
    templates: [],
    previewData: null,
    validationData: null,
    variables: null,
    loadingTemplate: true,
    loadingTemplates: false,
    loadingCreateTemplate: false,
    loadingPatchTemplate: false,
    loadingDeleteTemplate: false,
    loadingPreview: false,
    loadingValidate: false,
    loadingVariables: false,
    errorTemplate: false,
    errorTemplates: false,
    errorCreateTemplate: false,
    errorPatchTemplate: false,
    errorDeleteTemplate: false,
    errorPreview: false,
    errorValidate: false,
    errorVariables: false,
    count: 0,
    currentPage: 1,
    pageSize: 10,
});

const actions = {
    /**
     * Get template
     *
     * @param commit
     * @param id
     * @returns {Promise<void>}
     */
    async getTemplate({commit}, id) {
        try {
            await commit('templateDataBegin');
            const response = await DataService.get(`/productcommunicationtemplates/${id}`);
            await commit('templateDataSuccess', response.data);
        } catch (err) {
            await commit('templateDataErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij laden template');
        }
    },
    /**
     * Get templates list
     *
     * @param commit
     * @param params
     * @returns {Promise<void>}
     */
    getTemplates: paginatedListAction({endpoint: '/productcommunicationtemplates', prefix: 'templatesData'}),
    /**
     * Create template
     *
     * @param commit
     * @param data
     * @returns {Promise<void>}
     */
    async createTemplate({commit, dispatch}, data) {
        try {
            await commit('templateDataCreateBegin');
            const response = await DataService.post(`/productcommunicationtemplates`, data.value);
            await commit('templateDataCreateSuccess', response.data);
            await dispatch('getTemplates');
            addNotificationSuccess('Template aangemaakt');
            data.close();
        } catch (err) {
            await commit('templateDataCreateErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij aanmaken template');
        }
    },
    /**
     * Update template
     *
     * @param commit
     * @param data
     * @returns {Promise<void>}
     */
    async patchTemplate({commit, dispatch}, data) {
        try {
            await commit('templateDataPatchBegin');
            await DataService.patch(`/productcommunicationtemplates/${data.id}`, data);
            await commit('templateDataPatchSuccess');
            await dispatch('getTemplates');
            addNotificationSuccess('Template opgeslagen');
        } catch (err) {
            await commit('templateDataPatchErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij opslaan template');
        }
    },
    /**
     * Delete template
     *
     * @param commit
     * @param id
     * @returns {Promise<void>}
     */
    async deleteTemplate({commit, dispatch}, id) {
        try {
            await commit('templateDataDeleteBegin');
            await DataService.delete(`/productcommunicationtemplates/${id}`);
            await commit('templateDataDeleteSuccess');
            await dispatch('getTemplates');
            addNotificationSuccess('Template verwijderd');
        } catch (err) {
            await commit('templateDataDeleteErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij verwijderen template');
        }
    },
    /**
     * Preview email template
     *
     * @param commit
     * @param data
     * @returns {Promise<void>}
     */
    async previewEmailTemplate({commit}, data) {
        try {
            await commit('templatePreviewBegin');
            const response = await DataService.post(`/productcommunicationtemplates/${data.id}/preview/email`, {
                orderID: data.orderID,
                orderCommunicationID: data.orderCommunicationID,
                html: data.html,
            });
            await commit('templatePreviewSuccess', response.data);
        } catch (err) {
            await commit('templatePreviewErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij preview genereren');
        }
    },
    /**
     * Preview PDF template
     *
     * @param commit
     * @param data
     * @returns {Promise<void>}
     */
    async previewPdfTemplate({commit}, data) {
        try {
            await commit('templatePreviewBegin');
            const response = await DataService.post(`/productcommunicationtemplates/${data.id}/preview/pdf`, {
                orderID: data.orderID,
                html: data.html,
            }, {
                responseType: 'blob',
            });
            await commit('templatePreviewSuccess', response.data);
        } catch (err) {
            await commit('templatePreviewErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij preview genereren');
        }
    },
    /**
     * Validate template
     *
     * @param commit
     * @param data
     * @returns {Promise<void>}
     */
    async validateTemplate({commit}, data) {
        try {
            await commit('templateValidateBegin');
            const response = await DataService.post(`/productcommunicationtemplates/${data.id}/validate-template`, {
                html: data.html,
                type: data.type,
            });
            await commit('templateValidateSuccess', response.data);
        } catch (err) {
            await commit('templateValidateErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij valideren template');
        }
    },
    /**
     * Get template variables
     *
     * @param commit
     * @returns {Promise<void>}
     */
    async getTemplateVariables({commit}) {
        try {
            await commit('templateVariablesBegin');
            const response = await DataService.get('/productcommunicationtemplates/variables');
            await commit('templateVariablesSuccess', response.data);
        } catch (err) {
            await commit('templateVariablesErr', err);
            addNotificationError(err?.response?.data?.message || 'Fout bij laden variabelen');
        }
    },
    /**
     * Send test email
     *
     * @param _context
     * @param data
     * @returns {Promise<void>}
     */
    async sendTestEmail(_context, data) {
        try {
            // Ensure orderID is a number
            const orderID = data.orderID ? parseInt(data.orderID, 10) : null;
            
            if (!orderID || isNaN(orderID)) {
                throw new Error('Order ID is verplicht en moet een geldig nummer zijn');
            }
            
            const payload = {
                toEmail: data.toEmail,
                html: data.html,
                orderID: orderID,
            };
            
            await DataService.post(`/productcommunicationtemplates/${data.id}/test/email`, payload);
            addNotificationSuccess(`Test email succesvol verzonden naar ${data.toEmail}`);
            return { success: true };
        } catch (err) {
            let errorMessage = 'Fout bij versturen test email';
            
            if (err?.response?.data) {
                if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                } else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                } else {
                    errorMessage = JSON.stringify(err.response.data);
                }
            } else if (err?.message) {
                errorMessage = err.message;
            }
            
            addNotificationError(errorMessage);
            return { success: false, error: errorMessage };
        }
    },
    /**
     * Set current page
     *
     * @param commit
     * @param page
     */
    setCurrentPageTemplates({commit}, page) {
        commit('setCurrentPageTemplates', page);
    },
};

export default {
    namespaced: false,
    state,
    actions,
    mutations,
};

